import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { GitCommit, Activity } from 'lucide-react';
import logger from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Format a uptime span (in seconds) as a compact `Xd Yh Zm` string. */
function formatUptime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  const s = Math.floor(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

/**
 * Discreet build-version pill — fetches `/api/version` once on mount
 * and renders the short git SHA + commit time + a live backend-uptime
 * counter at the bottom of the Admin Page.
 *
 * Helps both the team and end-users verify whether they're running on
 * the latest deploy when triaging "is this a bug or a stale bundle?"
 * — the SHA shown here reflects the BACKEND deploy; if the JS bundle
 * is stale, this pill renders the backend's NEW SHA while a stale
 * bundle would have been built against an older one. Either way the
 * single SHA gives both parties a shared reference point.
 *
 * The uptime counter (recomputed every 60 s client-side from
 * `info.started_at`) makes "did the backend just restart?" obvious
 * when triaging session-loss / WebSocket-drop reports.
 */
export default function BuildVersionFooter() {
  const [info, setInfo] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/version`).then((res) => {
      if (!cancelled) setInfo(res.data || null);
    }).catch((err) => {
      logger.warn('Failed to fetch build version', err);
    });
    return () => { cancelled = true; };
  }, []);

  // Re-render the uptime label once a minute. Keeps the footer alive
  // without forcing a network round-trip.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (!info?.commit) return null;

  const when = info.commit_time
    ? new Date(info.commit_time).toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '';

  let uptimeLabel = '';
  if (info.started_at) {
    const startedMs = new Date(info.started_at).getTime();
    if (Number.isFinite(startedMs)) {
      uptimeLabel = formatUptime((now - startedMs) / 1000);
    }
  }

  return (
    <div
      className="mt-10 flex items-center justify-center gap-2 text-[10px] text-slate-400 select-text"
      data-testid="admin-build-version-footer"
      title={
        `Backend deploy SHA: ${info.commit}\n` +
        `Branch: ${info.branch || '—'}\n` +
        `Commit time: ${info.commit_time || '—'}\n` +
        `Backend started: ${info.started_at || '—'}\n\n` +
        'If this badge looks stale after a recent fix, hard-refresh ' +
        '(Ctrl/Cmd-Shift-R) to pick up the latest JS bundle.'
      }
    >
      <GitCommit className="w-2.5 h-2.5" />
      <span className="font-mono uppercase tracking-wide">
        Build {info.commit}
      </span>
      {info.branch && info.branch !== 'main' && (
        <span className="font-mono text-slate-300">· {info.branch}</span>
      )}
      {when && <span>· {when}</span>}
      {uptimeLabel && (
        <span
          className="inline-flex items-center gap-1 ml-1 text-slate-400"
          data-testid="admin-build-uptime"
          title={`Backend has been up since ${info.started_at}`}
        >
          <Activity className="w-2.5 h-2.5" />
          Up {uptimeLabel}
        </span>
      )}
    </div>
  );
}

